import * as angular from 'angular';
import * as _ from 'lodash';

import { ComponentDefinition, Group, IValueViewModel, SeriesSelectorViewModel } from '../../../models';
import { ISeriesSelectorService, ITableByGroupService } from '../../../services';
import { wtDroppableDrop, groupIdentifierSeparator, seriesAdded, defaultSeriesColor } from '../../../common';
import { TableByGroupWidgetSeries } from '../tableByGroup.series';

interface TableByGroupSeriesTuple {
    name: string;
    series: TableByGroupWidgetSeries;
    viewModel: IValueViewModel;
}

class TableByGroupSeriesController implements angular.IComponentController {
    isLoading: boolean;
    loadingMessage: string;
    series: TableByGroupWidgetSeries[];
    tuples: TableByGroupSeriesTuple[];
    sortConfig: any;

    readonly groups: Group[];
    readonly hasLegend: boolean;
    readonly viewModels: IValueViewModel[];

    private _seriesSelectorViewModels: SeriesSelectorViewModel[];

    static $inject = [
        '$scope',
        '$rootScope',
        'seriesSelectorService',
        'tableByGroupService'
    ];

    constructor(
        private _$scope: angular.IScope,
        private _$rootScope: angular.IRootScopeService,
        private _seriesSelectorService: ISeriesSelectorService,
        private _tableByGroupService: ITableByGroupService
    ) {
    }

    $onInit() {
        if (!this.series) {
            this.series = [];
        }

        this.sortConfig = {
            animation: 150,
            handle: '.drag-handle',
            onSort: evt => this.sort(evt),
        };

        this.isLoading = true;
        this._seriesSelectorService
            .getSeriesSelectorViewModels()
            .then((response) => {
                this._seriesSelectorViewModels = response;

                this._$scope.$watch(() => this.series, (newValue) => {
                    if (newValue && !!this.viewModels) {
                        this.mapSeriesToTuples(newValue);
                    }
                }, true);

                this._$scope.$on(seriesAdded,
                    (event, data) => {
                        this.addSeries(data);
                    });

                this._$scope.$on(wtDroppableDrop, (event, data) => {
                    if (data.itemId) {
                        this.drop(data);
                    }
                });
            })
            .finally(() => {
                this.isLoading = false;
            });
    }

    $onChanges(changesObj: angular.IOnChangesObject) {
        if (changesObj['viewModels'] && changesObj['viewModels'].currentValue && !!this.series) {
            this.mapSeriesToTuples(this.series);
        }
    }

    private mapSeriesToTuples(newValue: TableByGroupWidgetSeries[]) {
        this.tuples = newValue.map(d => {
            const ivalueViewModel = _.find(this.viewModels, vm => vm.guid === d.guid);
            return {
                name: ivalueViewModel
                    ? (ivalueViewModel.groupUniqueName
                        ? ivalueViewModel.groupUniqueName
                        + groupIdentifierSeparator + ivalueViewModel.name
                        : ivalueViewModel.name)
                    : d.guid,
                series: d,
                viewModel: ivalueViewModel
            };
        });
    }

    private sort(evt) {
        evt.models.forEach((s, i) => s.series.sortOrder = i);
        this.series = evt.models.map(d => d.series);
    }

    private drop(data: { itemId: string; binId?: string; }) {
        const guid = data.itemId.replace('id', '');
        const svm: SeriesSelectorViewModel = _.find(this._seriesSelectorViewModels, s => s.guid === guid);
        if (data.binId) {
            const bin = data.binId.split('+');
            this.updateSerie(bin[0], bin[1], svm);
        } else {
            this.addSeries(svm);
        }
    }

    private updateSerie(oldValue: string, oldGuid: string, svm: SeriesSelectorViewModel) {
        const old = _.find(this.series, s => s.guid === oldGuid && s.value === oldValue);
        const newValueType = this._seriesSelectorService.getAvailableValueType(svm, this.series, old);
        if (newValueType) {
            old.guid = svm.guid;
            old.value = newValueType;
            old.isBoundToGroup = false;

            const guid = svm.groupList[0].guid;
            this._tableByGroupService
                .getFirstAncestorWithUniqueName(guid)
                .then(data => {
                    this._$rootScope.$broadcast('identifierAdded', { group: data.group });
                });
        }
    }

    remove(series: TableByGroupWidgetSeries): void {
        _.remove(this.series, s => s.guid === series.guid && s.value === series.value);
    }

    private addSeries(svm: SeriesSelectorViewModel): void {
        const color = defaultSeriesColor;
        const serie = new TableByGroupWidgetSeries(svm.guid);
        const existing = this.series.filter(s => s.guid === svm.guid);
        const possibleValues = this._seriesSelectorService.getValues(svm as any, true);
        let canAdd = false;

        if (existing && existing.length > 0) {
            const value = _.find(possibleValues, v => _.every(existing, e => e.value !== v.key));
            if (value) {
                serie.value = value.key;
                canAdd = true;
            }
        } else {
            if (possibleValues && possibleValues.length > 0) {
                serie.value = possibleValues[0].key;
            }
            canAdd = true;
        }
        if (canAdd) {
            if (!serie.value) {
                serie.value = possibleValues[0].key;
            }
            serie.color = color;
            const guid = svm.groupList[0].guid;
            this._tableByGroupService
                .getFirstAncestorWithUniqueName(guid)
                .then(data => {
                    const maxSortOrder = _.max(this.series.map(serie => serie.sortOrder));
                    serie.sortOrder = isNaN(maxSortOrder) ? 0 : maxSortOrder + 1;
                    this.series.push(serie);
                    this._$rootScope.$broadcast('identifierAdded', { group: data.group });
                });
        }
    }
}

export const wtTableByGroupSeries: ComponentDefinition = {
    componentId: 'wtTableByGroupSeries',
    controller: TableByGroupSeriesController,
    template: require('./tableByGroupSeries.component.html'),
    controllerAs: 'vm',
    bindings: {
        groups: '<',
        hasLegend: '<',
        series: '=',
        viewModels: '<'
    }
};